import { Document } from "flexsearch";

// Client-side application source. Compiled to assets/js/app.js by build-client.mjs.

type SearchField = "all" | "composer" | "interpreter" | "place" | "series";

interface ProgrammeRecord {
    id: string;
    slug: string;
    title: string;
    collection: string;
    composer?: string[];
    interpreter?: string[];
    place?: string[];
    series?: string;
    years?: number[];
    dates?: string[];
    images?: string[];
    [field: string]: unknown;
}

interface SearchIndexManifest {
    files: Array<{ key: string; file: string }>;
}

interface SearchData {
    index: any;
    records: ProgrammeRecord[];
    byId: Map<string, ProgrammeRecord>;
}

const SEARCH_OPTIONS = {
    document: {
        id: "id",
        index: [
            { field: "all", tokenize: "strict", resolution: 3 },
            { field: "composer", tokenize: "strict", resolution: 3 },
            { field: "interpreter", tokenize: "strict", resolution: 3 },
            { field: "place", tokenize: "strict", resolution: 3 },
            { field: "series", tokenize: "strict", resolution: 3 },
        ],
    },
};

const COLLECTIONS = {
    ch_gc: "Conservatoire de Genève",
    ch_gm: "La Musicale – Genève",
    ch_lac: "Conservatoire de Lausanne",
    ch_famb: "Freunde Alter Musik Basel",
};

const FACETS = [
    ["composer", "Composers"],
    ["interpreter", "Interpreters"],
    ["place", "Places"],
    ["collection", "Collections"],
    ["series", "Series"],
    ["years", "Years"],
];

const TRANSLATIONS = {
    en: {
        pages: "Pages",
        languages: "Languages",
        back: "← Search programmes",
        places: "Places",
        composers: "Composers",
        interpreters: "Interpreters",
        notes: "Notes",
        signature: "Signature",
        collection: "Collection",
        search: "Search",
        archive: "Archive",
        browse: "Browse",
        selectIndex: "Select index",
        dateRange: "Date Range",
        fastNav: "Fast nav",
        series: "Series",
        years: "Years",
        noIndex: "No index available",
    },
    de: {
        pages: "Seiten",
        languages: "Sprachen",
        back: "← Zur Suche",
        places: "Orte",
        composers: "Komponisten",
        interpreters: "Interpreten",
        notes: "Anmerkungen",
        signature: "Signatur",
        collection: "Sammlung",
        search: "Suche",
        archive: "Archiv",
        browse: "Stöbern",
        selectIndex: "Wählen Sie einen Index",
        dateRange: "Datumsbereich",
        fastNav: "Schnellnavigation",
        series: "Serien",
        years: "Jahr",
        noIndex: "Kein Index verfügbar",
    },
    fr: {
        pages: "Pages",
        languages: "Langages",
        back: "← Retour à la recherche",
        places: "Lieux",
        composers: "Compositeurs",
        interpreters: "Interprètes",
        notes: "Notes",
        signature: "Cote",
        collection: "Collection",
        search: "Recherche",
        archive: "Documents épinglés",
        browse: "Naviguer",
        selectIndex: "Sélectionner l’index",
        dateRange: "Période",
        fastNav: "Navigation rapide",
        series: "Séries",
        years: "Année",
        noIndex: "Aucun index disponible",
    },
    it: {
        pages: "Pagine",
        languages: "Lingue",
        back: "← Torna alla ricerca",
        places: "Luoghi",
        composers: "Compositori",
        interpreters: "Interpreti",
        notes: "Note",
        signature: "Segnatura",
        collection: "Collezione",
        search: "Ricerca",
        archive: "Archivio",
        browse: "Consulta",
        selectIndex: "Seleziona l’indice",
        dateRange: "Intervallo di date",
        fastNav: "Navigazione rapida",
        series: "Serie",
        years: "Anno",
        noIndex: "Nessun indice disponibile",
    },
};

let searchPromise: Promise<SearchData> | undefined;

const esc = (value) =>
    String(value ?? "").replace(
        /[&<>"]/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
    );

const language = () =>
    localStorage.getItem("onstage-language") || navigator.language?.slice(0, 2) || "en";

const t = (key) => TRANSLATIONS[language()]?.[key] || TRANSLATIONS.en[key] || key;

function translatePage() {
    document.documentElement.lang = ["en", "de", "fr", "it"].includes(language())
        ? language()
        : "en";
    document.querySelectorAll("[data-i18n]").forEach((element) => {
        element.textContent = t(element.dataset.i18n);
    });
}

const recordUrl = (record) => `/source/${encodeURIComponent(record.slug)}/`;

const formatDates = (record) =>
    [...(record.dates || [])]
        .sort()
        .map((d) =>
            d.length === 8 ? `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}` : d,
        )
        .join(" - ");

function initialiseLanguage() {
    const select = document.querySelector("#site-language");
    if (!select) return;
    select.value = ["en", "de", "fr", "it"].includes(language()) ? language() : "en";
    select.addEventListener("change", () => {
        localStorage.setItem("onstage-language", select.value);
        window.location.reload();
    });
    const context = document.querySelector("#context-bar");
    const driver = context?.querySelector(".context-driver");
    driver?.addEventListener("click", () => {
        context.classList.toggle("visible");
        driver.setAttribute(
            "aria-expanded",
            String(context.classList.contains("visible")),
        );
    });
    const saved = JSON.parse(localStorage.getItem("onstage-context") || "{}");
    document.querySelectorAll(".collection-context input").forEach((input) => {
        input.checked = saved.collections
            ? saved.collections.includes(input.value)
            : input.checked;
        input.addEventListener("change", persistContext);
    });
    document.querySelectorAll(".date-context input").forEach((input) => {
        input.max = "2024";
        if (saved[input.name]) input.value = saved[input.name];
        input.addEventListener("change", persistContext);
    });
    const collectionSelect = document.querySelector("#site-collection");
    const syncCollectionSelect = () => {
        if (!collectionSelect) return;
        const inputs = [...document.querySelectorAll(".collection-context input")],
            selected = inputs
                .filter((input) => input.checked)
                .map((input) => input.value);
        collectionSelect.value =
            selected.length === inputs.length
                ? "all"
                : selected.length === 1
                  ? selected[0]
                  : "custom";
    };
    collectionSelect?.addEventListener("change", () => {
        const value = collectionSelect.value;
        document.querySelectorAll(".collection-context input").forEach((input) => {
            input.checked = value === "all" || input.value === value;
        });
        persistContext();
        syncCollectionSelect();
    });
    document.addEventListener("onstage-contextchange", syncCollectionSelect);
    syncCollectionSelect();
    updatePinBadge();
}

function persistContext() {
    const context = {
        collections: [
            ...document.querySelectorAll(".collection-context input:checked"),
        ].map((input) => input.value),
        from: document.querySelector('.date-context [name="from"]')?.value,
        to: document.querySelector('.date-context [name="to"]')?.value,
    };
    localStorage.setItem("onstage-context", JSON.stringify(context));
    document.dispatchEvent(new Event("onstage-contextchange"));
}

function updatePinBadge() {
    const badge = document.querySelector("[data-pin-count]");
    if (!badge) return;
    const count = pinned().length;
    badge.hidden = !count;
    badge.textContent = count;
}

async function loadStaticPage(element) {
    const key = element.dataset.staticPage;
    const lang = language();
    try {
        const response = await fetch(`/assets/content/${key}.${lang}.html`);
        if (!response.ok) throw new Error();
        element.innerHTML = await response.text();
    } catch {
        const response = await fetch(`/assets/content/${key}.en.html`);
        element.innerHTML = response.ok
            ? await response.text()
            : "<p>Resource not found.</p>";
    }
}

async function loadSearch(): Promise<SearchData> {
    if (searchPromise) return searchPromise;
    searchPromise = (async () => {
        const [records, manifest] = (await Promise.all([
            fetch("/assets/search/records.json").then((r) => r.json()),
            fetch("/assets/search/index-manifest.json").then((r) => r.json()),
        ])) as [ProgrammeRecord[], SearchIndexManifest];
        const index = new (Document as any)(SEARCH_OPTIONS);
        await Promise.all(
            manifest.files.map(async ({ key, file }) =>
                index.import(
                    key,
                    await fetch(`/assets/search/${file}`).then((r) => r.text()),
                ),
            ),
        );
        return {
            index,
            records,
            byId: new Map(records.map((record) => [record.id, record])),
        };
    })();
    return searchPromise;
}

function imageUrl(record: ProgrammeRecord) {
    return record.images?.[0]
        ? `https://iiif.rism.digital/image/lausanne/${record.images[0]}/full/120,/0/default.jpg`
        : "";
}

function card(record: ProgrammeRecord) {
    const image = imageUrl(record);
    return `<article class="result">${image ? `<img src="${esc(image)}" alt="">` : "<div></div>"}<div><h3><a href="${recordUrl(record)}">${esc(record.title)}</a></h3><p>${esc((record.place || []).join(" · ") || "[s.l.]")}<br>${esc(formatDates(record))}</p></div><button class="secondary pin" data-pin="${esc(record.id)}">Pin</button></article>`;
}

function pinned() {
    try {
        return JSON.parse(localStorage.getItem("onstage-pins") || "[]");
    } catch {
        return [];
    }
}

function togglePin(id) {
    const ids = pinned();
    localStorage.setItem(
        "onstage-pins",
        JSON.stringify(
            ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id],
        ),
    );
    updatePinBadge();
}

function constraintsMarkup() {
    return "";
}

function filterRecords(records, state) {
    const selectedCollections = [
        ...document.querySelectorAll(".collection-context input:checked"),
    ].map((input) => input.value);
    const from =
        Number(document.querySelector('.date-context [name="from"]')?.value) ||
        -Infinity;
    const to =
        Number(document.querySelector('.date-context [name="to"]')?.value) || Infinity;
    return records.filter(
        (record) =>
            selectedCollections.includes(record.collection) &&
            (record.years || []).some((year) => year >= from && year <= to) &&
            [...state.filters].every(([field, value]) =>
                (Array.isArray(record[field]) ? record[field] : [record[field]])
                    .map(String)
                    .includes(value),
            ),
    );
}

function facetValues(records, field) {
    const counts = new Map();
    records.forEach((record) => {
        const values =
            field === "years"
                ? record.years
                : field === "collection"
                  ? [record.collection]
                  : record[field];
        (Array.isArray(values) ? values : [values])
            .filter(Boolean)
            .forEach((value) =>
                counts.set(String(value), (counts.get(String(value)) || 0) + 1),
            );
    });
    return [...counts].sort((a, b) => a[0].localeCompare(b[0], "fr"));
}

async function searchIds(index, term, field) {
    if (!term.trim()) return null;
    const result = await index.search(term, { index: field, limit: 20000 });
    return new Set(result.flatMap((group) => group.result));
}

function bindPins(root) {
    root.querySelectorAll("[data-pin]").forEach((button) =>
        button.addEventListener("click", () => {
            togglePin(button.dataset.pin);
            button.textContent = pinned().includes(button.dataset.pin)
                ? "Unpin"
                : "Pin";
        }),
    );
}

function initialiseSearch(root) {
    root.innerHTML = `<h2 class="browse-title">${t("search")}</h2><form class="search-form"><input name="query" aria-label="Search terms" placeholder="Search programmes and OCR text"><select name="field"><option value="all">Any field</option><option value="composer">Composer</option><option value="interpreter">Interpreter</option><option value="place">Place</option><option value="series">Series</option></select><button>Search</button></form>${constraintsMarkup()}<div class="search-status">Search is ready when you submit a query.</div><div class="search-grid"><div class="results"></div><aside class="facets"></aside></div>`;
    const state = { root, filters: new Map(), ids: null, term: "", field: "all" };
    const run = async () => {
        root.querySelector(".search-status").textContent = "Loading search index…";
        const { index, records } = await loadSearch();
        state.term = root.querySelector('[name="query"]').value;
        state.field = root.querySelector('[name="field"]').value;
        state.ids = await searchIds(index, state.term, state.field);
        const matched = state.ids
            ? records.filter((record) => state.ids.has(record.id))
            : records;
        const constrained = filterRecords(matched, state).sort(
            (a, b) => (a.years?.[0] || 9999) - (b.years?.[0] || 9999),
        );
        root.querySelector(".search-status").textContent =
            `${constrained.length} programme${constrained.length === 1 ? "" : "s"} found`;
        root.querySelector(".results").innerHTML =
            `<p class="results-meta">Showing the first ${Math.min(100, constrained.length)} results.</p>${constrained.slice(0, 100).map(card).join("") || "<p>No results found.</p>"}`;
        bindPins(root);
        root.querySelector(".facets").innerHTML = FACETS.map(([field, label]) => {
            const values = facetValues(constrained, field).slice(0, 80);
            return `<section><h2>${label}</h2><div class="facet-list">${values.map(([value, count]) => `<button data-facet-field="${field}" data-facet-value="${esc(value)}"><span>${esc(field === "collection" ? COLLECTIONS[value] || value : value)}</span><span>${count}</span></button>`).join("") || "<small>No values</small>"}</div></section>`;
        }).join("");
        root.querySelectorAll("[data-facet-field]").forEach((button) =>
            button.addEventListener("click", () => {
                const key = button.dataset.facetField;
                const value = button.dataset.facetValue;
                state.filters.has(key) && state.filters.get(key) === value
                    ? state.filters.delete(key)
                    : state.filters.set(key, value);
                run();
            }),
        );
    };
    root.querySelector("form").addEventListener("submit", (event) => {
        event.preventDefault();
        run().catch((error) => {
            root.querySelector(".search-status").textContent =
                `Search could not be loaded: ${error.message}`;
        });
    });
    root.querySelectorAll("input,select").forEach((input) =>
        input.addEventListener("change", () => {
            if (state.ids !== null) run();
        }),
    );
    document.addEventListener("onstage-contextchange", () => {
        if (state.ids !== null) run();
    });
}

function initialiseBrowse(root) {
    const state = {
        field: "composer",
        terms: [],
        records: [],
        selected: null,
        page: 0,
    };
    const labelFor = (field) =>
        ({
            composer: t("composers"),
            interpreter: t("interpreters"),
            place: t("places"),
            series: t("series"),
            years: t("years"),
        })[field];
    const valuesFor = (record) =>
        state.field === "years" ? record.years || [] : record[state.field] || [];
    const groupFor = (value) =>
        state.field === "years"
            ? `${Math.floor(Number(value) / 20) * 20}`
            : String(value).charAt(0).toUpperCase();
    const renderIndex = () => {
        const groups = [...new Set(state.terms.map(([value]) => groupFor(value)))];
        let lastGroup = null;
        const from =
                document.querySelector('.date-context [name="from"]')?.value || "1800",
            to = document.querySelector('.date-context [name="to"]')?.value || "2024";
        root.innerHTML = `<div class="browse-header"><h2 class="browse-title">Browse</h2><div class="browse-controls"><label>Select index<select name="field"><option value="composer">Composers</option><option value="interpreter">Interpreters</option><option value="place">Places</option><option value="series">Series</option><option value="years">Years</option></select></label><fieldset class="browse-date-range"><legend>Date Range</legend><div><input name="from" type="number" min="1800" max="2024" value="${esc(from)}"><input name="to" type="number" min="1800" max="2024" value="${esc(to)}"></div><div class="browse-range-inputs"><span class="browse-range-track" aria-hidden="true"><span class="browse-range-fill"></span></span><input name="from-range" aria-label="Start year" type="range" min="1800" max="2024" value="${esc(from)}"><input name="to-range" aria-label="End year" type="range" min="1800" max="2024" value="${esc(to)}"></div></fieldset></div>${state.terms.length ? `<nav class="browse-fast-nav"><b>Fast nav:</b>${groups.map((group) => `<a href="#browse-${encodeURIComponent(group)}">${esc(group)}</a>`).join("")}</nav>` : ""}</div><div class="browse-index">${
            state.terms
                .map(([value, count], position) => {
                    const group = groupFor(value);
                    const heading =
                        group !== lastGroup
                            ? ((lastGroup = group),
                              `<div class="browse-group" id="browse-${encodeURIComponent(group)}"><h2>${esc(state.field === "years" ? `${group}–${Number(group) + 19}` : group)}</h2></div>`)
                            : "";
                    return `${heading}<button type="button" class="browse-term" data-position="${position}"><span>${esc(value)}</span></button>`;
                })
                .join("") || "<h3>No index available.</h3>"
        }</div>`;
        root.querySelector('[name="field"]').value = state.field;
        const indexLabel = root.querySelector(".browse-controls > label");
        if (indexLabel?.firstChild) indexLabel.firstChild.nodeValue = t("selectIndex");
        root.querySelector(".browse-date-range legend").textContent = t("dateRange");
        root.querySelector(".browse-fast-nav b")?.replaceChildren(`${t("fastNav")}:`);
        root.querySelectorAll('[name="field"] option').forEach((option) => {
            option.textContent = labelFor(option.value);
        });
        root.querySelector(".browse-index h3")?.replaceChildren(t("noIndex"));
        root.querySelector('[name="field"]').addEventListener("change", (event) =>
            loadIndex(event.target.value),
        );
        const updateDateRange = (event) => {
            const name = event.target.name.startsWith("from") ? "from" : "to";
            let fromValue = Number(root.querySelector('[name="from-range"]').value),
                toValue = Number(root.querySelector('[name="to-range"]').value);
            if (name === "from") fromValue = Number(event.target.value);
            else toValue = Number(event.target.value);
            if (fromValue > toValue) {
                if (name === "from") toValue = fromValue;
                else fromValue = toValue;
            }
            root.querySelectorAll('[name="from"], [name="from-range"]').forEach(
                (control) => (control.value = String(fromValue)),
            );
            root.querySelectorAll('[name="to"], [name="to-range"]').forEach(
                (control) => (control.value = String(toValue)),
            );
            const range = root.querySelector(".browse-range-inputs"),
                min = 1800,
                max = 2024;
            range.style.setProperty(
                "--range-start",
                `${((fromValue - min) / (max - min)) * 100}%`,
            );
            range.style.setProperty(
                "--range-end",
                `${((toValue - min) / (max - min)) * 100}%`,
            );
            document.querySelector('.date-context [name="from"]').value =
                String(fromValue);
            document.querySelector('.date-context [name="to"]').value = String(toValue);
        };
        root.querySelectorAll('.browse-date-range input[type="range"]').forEach(
            (input) => {
                input.addEventListener("input", updateDateRange);
                input.addEventListener("change", () => {
                    localStorage.setItem("onstage-date-range-custom", "true");
                    persistContext();
                });
            },
        );
        root.querySelectorAll('.browse-date-range input[type="number"]').forEach(
            (input) =>
                input.addEventListener("change", (event) => {
                    localStorage.setItem("onstage-date-range-custom", "true");
                    updateDateRange(event);
                    persistContext();
                }),
        );
        updateDateRange({ target: root.querySelector('[name="from-range"]') });
        root.querySelectorAll(".browse-term").forEach((button) =>
            button.addEventListener("click", () =>
                showTerm(Number(button.dataset.position)),
            ),
        );
    };
    const renderTerm = () => {
        const [value] = state.terms[state.selected];
        const matches = state.records
            .filter((record) => valuesFor(record).map(String).includes(String(value)))
            .sort((a, b) => (a.years?.[0] || 9999) - (b.years?.[0] || 9999));
        const perPage = 100,
            totalPages = Math.max(1, Math.ceil(matches.length / perPage)),
            start = state.page * perPage;
        root.innerHTML = `<div class="browse-header browse-result-header"><div class="breadcrumbs"><button type="button" class="browse-back">Browse</button><span>/</span><span>${esc(labelFor(state.field))}</span></div><div class="browse-actions"><button type="button" class="secondary browse-prev" ${state.selected === 0 ? "disabled" : ""}>« Previous</button><button type="button" class="secondary browse-next" ${state.selected + 1 === state.terms.length ? "disabled" : ""}>Next »</button><a class="button" href="/search/">Search</a></div><h2>${esc(value)}</h2><p class="results-meta">${matches.length} programme${matches.length === 1 ? "" : "s"} found</p></div><div class="browse-result-list">${
            matches
                .slice(start, start + perPage)
                .map(card)
                .join("") || "<p>No results found.</p>"
        }</div>${totalPages > 1 ? `<nav class="browse-pagination"><button type="button" class="secondary browse-page-prev" ${state.page === 0 ? "disabled" : ""}>« Previous</button><span>Page ${state.page + 1} of ${totalPages}</span><button type="button" class="secondary browse-page-next" ${state.page + 1 === totalPages ? "disabled" : ""}>Next »</button></nav>` : ""}`;
        root.querySelector(".browse-back").addEventListener("click", () => {
            state.selected = null;
            state.page = 0;
            renderIndex();
        });
        root.querySelector(".browse-prev").addEventListener("click", () =>
            showTerm(state.selected - 1),
        );
        root.querySelector(".browse-next").addEventListener("click", () =>
            showTerm(state.selected + 1),
        );
        root.querySelector(".browse-page-prev")?.addEventListener("click", () => {
            state.page -= 1;
            renderTerm();
        });
        root.querySelector(".browse-page-next")?.addEventListener("click", () => {
            state.page += 1;
            renderTerm();
        });
        bindPins(root);
    };
    const showTerm = (position) => {
        state.selected = position;
        state.page = 0;
        renderTerm();
        window.scrollTo(0, 0);
    };
    const loadIndex = async (field = state.field) => {
        state.field = field;
        state.selected = null;
        state.page = 0;
        root.innerHTML = '<p class="search-status">Loading index…</p>';
        try {
            const { records } = await loadSearch();
            if (localStorage.getItem("onstage-date-range-custom") !== "true") {
                const selectedCollections = [
                    ...document.querySelectorAll(".collection-context input:checked"),
                ].map((input) => input.value);
                const years = records
                    .filter((record) => selectedCollections.includes(record.collection))
                    .flatMap((record) => record.years || [])
                    .filter((year) => year >= 1800 && year <= 2024);
                if (years.length) {
                    const from = Math.min(...years),
                        to = Math.max(...years);
                    document.querySelector('.date-context [name="from"]').value =
                        String(from);
                    document.querySelector('.date-context [name="to"]').value =
                        String(to);
                    const saved = JSON.parse(
                        localStorage.getItem("onstage-context") || "{}",
                    );
                    localStorage.setItem(
                        "onstage-context",
                        JSON.stringify({
                            ...saved,
                            collections: selectedCollections,
                            from,
                            to,
                        }),
                    );
                }
            }
            state.records = filterRecords(records, { root, filters: new Map() });
            state.terms = facetValues(state.records, state.field);
            renderIndex();
        } catch (error) {
            root.innerHTML = `<p class="search-status">Browse could not be loaded: ${esc(error.message)}</p>`;
        }
    };
    document.addEventListener("onstage-contextchange", () => loadIndex());
    loadIndex();
}

async function initialisePins(root) {
    root.innerHTML = `<h2 class="browse-title">${t("archive")}</h2><p>Loading pinned programmes…</p>`;
    const { byId } = await loadSearch();
    const records = pinned()
        .map((id) => byId.get(id))
        .filter(Boolean);
    root.innerHTML = `<h2 class="browse-title">${t("archive")}</h2>${records.length ? records.map(card).join("") : "<p>No programmes pinned yet.</p>"}`;
    bindPins(root);
}

function initialiseViewer(element, index) {
    const manifestUrl = `https://iiif.rism.digital/manifest/onstage/${element.dataset.manifest.replace(/\.xml$/, ".json")}`;
    const viewerId = `diva-viewer-${index}`;
    element.innerHTML = `<div id="${viewerId}" class="diva-viewer"></div>`;
    try {
        if (!window.Diva || !window.OpenSeadragon)
            throw new Error("Diva.js assets were not loaded");
        new window.Diva(viewerId, { objectData: manifestUrl, showTitle: false });
        document.getElementById(viewerId).classList.add("diva-viewer");
    } catch (error) {
        element.innerHTML = `<p>The IIIF viewer is currently unavailable. <a href="${esc(manifestUrl)}" target="_blank" rel="noreferrer">Open manifest</a></p>`;
        console.error(error);
    }
}

initialiseLanguage();
translatePage();
document.querySelectorAll("[data-static-page]").forEach(loadStaticPage);
document.querySelectorAll(".viewer[data-manifest]").forEach(initialiseViewer);

const searchRoot = document.querySelector("#search-app");
if (searchRoot) initialiseSearch(searchRoot);

const browseRoot = document.querySelector("#browse-app");
if (browseRoot) initialiseBrowse(browseRoot);

const pinRoot = document.querySelector("#pin-app");
if (pinRoot) initialisePins(pinRoot);
